import classNames from "classnames";

import {
  AscBadgeColor,
  pluralizeIfNecessary,
  useOutsideClick,
} from "@ascendnow/shared";
import AscAdminAction from "../../AscAdminAction/AscAdminAction";
import AscBadge from "../../AscBadge/AscBadge";
import AscProgressBar from "../../AscProgressBar/AscProgressBar";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import {
  AwardIcon,
  CheckDisabledIcon,
  LoadingSpinnerIcon,
  MoreIcon,
} from "../../AscIcons";

import styles from "./AscSkillCard.module.scss";
import AscImage from "../../AscImage/AscImage";

export interface AscSkillCardProps {
  skillId?: string;
  title: string;
  percentComplete?: number;
  started?: boolean;
  modules?: number;
  badgeColor?: AscBadgeColor;
  className?: string;
  variant?: "Admin" | "Platform";
  imageUrl?: string;
  loading?: boolean;
  startLoading?: boolean;
  onStart?: (skillId?: string) => void;
  onCardEdit?: () => void;
  onCardDelete?: () => void;
  onClick?: () => void;
}

export default function AscSkillCard({
  skillId,
  title,
  percentComplete = 0,
  modules = 0,
  badgeColor = "TEAL",
  className,
  variant,
  imageUrl,
  loading = false,
  started = false,
  startLoading = false,
  onStart,
  onCardEdit,
  onCardDelete,
  onClick,
}: AscSkillCardProps) {
  const { isVisible, ref, setIsVisible } =
    useOutsideClick<HTMLDivElement>(false);

  const handleMoreAction = () => {
    setIsVisible((prev) => !prev);
  };

  function handleStart(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.preventDefault();
    e.stopPropagation();
    onStart?.(skillId);
  }

  return (
    <AscGenericCard
      ref={ref}
      className={classNames(
        styles.ground,
        {
          [styles.completed]: percentComplete >= 100,
          [styles.notStarted]: !started,
          [styles.adminGround]: variant === "Admin",
          [styles.loading]: loading,
          [styles.clickable]: !!onClick,
        },
        className
      )}
      onClick={onClick}
      loading={loading}
      titleClassName={styles.cardTitle}
      bodyClassName={styles.cardBody}
      title={title}
      customImage={
        <div className={styles.topContent}>
          {!imageUrl ? (
            <AscBadge color={badgeColor} className={styles.badge} />
          ) : (
            <AscImage
              src={imageUrl}
              alt={imageUrl}
              className={styles.badgeImage}
            />
          )}
          {variant !== "Admin" ? (
            <CheckDisabledIcon
              className={styles.checkIcon}
              data-testid={"asc-skill-card-check-mark-icon"}
            />
          ) : (
            <>
              {(onCardDelete || onCardEdit) && (
                <MoreIcon
                  className={styles.moreIcon}
                  data-testid={"asc-skill-card-more-icon"}
                  onClick={handleMoreAction}
                />
              )}
              {isVisible && (
                <AscAdminAction
                  option1={"Edit"}
                  option2={"Delete"}
                  clickOption1={onCardEdit}
                  clickOption2={onCardDelete}
                  className={styles.adminAction}
                />
              )}
            </>
          )}
        </div>
      }
    >
      {variant !== "Admin" ? (
        <div className={styles.progressContent}>
          <AscProgressBar value={percentComplete} max={100} fullWidth />
          <div className={styles.bottomContent}>
            <div className={styles.percentageTextContainer}>
              Completed:
              <span className={styles.percentage}>
                {percentComplete?.toFixed(2)}%
              </span>
            </div>
            <div className={styles.modulesTextContainer}>
              <AwardIcon className={styles.moduleIcon} />
              <span className={styles.modules}>
                {modules} {pluralizeIfNecessary("Module", modules)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.modulesTextContainer}>
          <AwardIcon className={styles.moduleIcon} />
          <span className={styles.modules}>
            {modules} {pluralizeIfNecessary("Module", modules)}
          </span>
        </div>
      )}

      {startLoading ? (
        <LoadingSpinnerIcon className={styles.startLoadingIcon} />
      ) : (
        variant !== "Admin" && (
          <div className={styles.startLabel} onClick={handleStart}>
            Start
          </div>
        )
      )}
    </AscGenericCard>
  );
}
